import { toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@0xtorch/evm'
import { createSolanaActionSource } from '@0xtorch/solana'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import {
  mexcAccountCategory,
  networkEvmChainIds,
  solanaNetwork,
} from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/api/v3/capital/withdraw/history',
    query: {
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // 昇順 sort by applyTime
  const sortedWithdrawals = response.sort((a, b) => a.applyTime - b.applyTime)

  // データ処理
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedWithdrawals) {
    // Skip failed or canceled withdrawals (status 8: FAILED, 9: CANCEL)
    if (item.status === 8 || item.status === 9) {
      continue
    }

    // Stop processing if withdrawal is not successful (status 7: SUCCESS)
    // Status 1: APPLY, 2: AUDITING, 3: WAIT, 4: PROCESSING, 5: WAIT_PACKAGING, 6: WAIT_CONFIRM, 10: MANUAL
    if (
      item.status === 1 ||
      item.status === 2 ||
      item.status === 3 ||
      item.status === 4 ||
      item.status === 5 ||
      item.status === 6 ||
      item.status === 10
    ) {
      stopped = true
      break
    }

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined
        ? item.applyTime
        : Math.min(fetchedFrom, item.applyTime)
    fetchedTo =
      fetchedTo === undefined
        ? item.applyTime
        : Math.max(fetchedTo, item.applyTime)

    // Create fee action if transactionFee > 0
    if (item.transactionFee.value > 0n) {
      const asset = getAsset(item.coin.split('-')[0])
      if (!asset) {
        throw new AssetNotFoundException(item.coin.split('-')[0])
      }

      actions.push({
        comment: `Withdraw ${toStringBigDecimal(item.amount)} [${item.coin}] | network: ${item.network} | tx: ${item.txId}`,
        evidence: 'system-rule',
        order: 0,
        source: `${mexcAccountCategory}_${config.type}_${item.id}`,
        type: 'fee',
        timestamp: item.applyTime,
        transfers: [
          {
            asset,
            amount: item.transactionFee,
            direction: 'out',
          },
        ],
      })
    }

    // Create EVM source if applicable
    if (item.network && item.txId) {
      const evmChainId = networkEvmChainIds[item.network]
      const txId = item.txId.split(':')[0] // Remove any potential network prefix
      if (evmChainId && isHex(txId)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: txId,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (item.network === solanaNetwork && item.txId) {
      sources.push(createSolanaActionSource({ signature: item.txId }))
    }
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
